import React, { useState, useEffect, useRef } from 'react';
import logo from '../Logos/escudoPriballUniverso.png';
import instagramLogo from '../Logos/logoInstragram.png';
import facebookLogo from '../Logos/logofacebook.png';
import whatsappLogo from '../Logos/logowhatsApp.png';
import './Home.css';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Spinner from './Spinner';
import { Link } from 'react-router-dom';
import { uploadImage } from '../firebase';
import Subscription from './Subscription';
import { useEditMode } from '../contexts/EditModeContext';
import { isStaffOrViewerFromStorage } from '../utils/staffAccess';

const Home = () => {
  const [content, setContent] = useState({
    items: [], // Array unificado que mantiene el orden cronológico
    instagram: '',
    facebook: '',
    whatsapp: ''
  });

  const [isLoading, setIsLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState('');
  const particlesRef = useRef(null);
  const footballBallsRef = useRef([]);
  const { isEditMode } = useEditMode();
  const showClientNewsletterSection = !isStaffOrViewerFromStorage();

  useEffect(() => {
    const fetchHomeInfo = async () => {
      setIsLoading(true);
      try {
        const docRef = doc(db, 'HomeInfo', 'main');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Migrar datos antiguos a nueva estructura si es necesario
          if (data.items) {
            setContent(data);
          } else {
            // Migración de estructura antigua
            const items = [];
            
            // Migrar textos
            if (data.texts) {
              data.texts.forEach(text => {
                items.push({
                  id: Date.now() + Math.random(),
                  type: 'text',
                  text: text.text || '',
                  headerType: text.headerType || 'h2',
                  fontType: text.fontType || 'conventional',
                  color: text.color || '#ffffff',
                  link: text.link || '',
                  tooltip: text.tooltip || ''
                });
              });
            }
            
            // Migrar imágenes
            if (data.images) {
              data.images.forEach((image, index) => {
                items.push({
                  id: Date.now() + Math.random(),
                  type: 'image',
                  url: image,
                  link: data.imageLinks?.[index] || '',
                  tooltip: data.imageTooltips?.[index] || ''
                });
              });
            }
            
            // Migrar videos
            if (data.videos) {
              data.videos.forEach(video => {
                items.push({
                  id: Date.now() + Math.random(),
                  type: 'video',
                  url: video.url || '',
                  title: video.title || '',
                  description: video.description || ''
                });
              });
            }
            
            setContent({
              items,
              instagram: data.instagram || '',
              facebook: data.facebook || '',
              whatsapp: data.whatsapp || ''
            });
          }
        }
      } catch (error) {
        console.error('Error fetching home info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHomeInfo();
  }, []);

  // Efecto para crear partículas dinámicas
  useEffect(() => {
    const createParticles = () => {
      if (particlesRef.current) {
        // Limpiar partículas existentes
        particlesRef.current.innerHTML = '';

        // Crear nuevas partículas
        for (let i = 0; i < 30; i++) {
          const particle = document.createElement('div');
          particle.className = 'particle';
          particle.style.left = Math.random() * 100 + '%';
          particle.style.animationDelay = Math.random() * 15 + 's';
          particle.style.animationDuration = (Math.random() * 10 + 15) + 's';
          particlesRef.current.appendChild(particle);
        }
      }
    };

    const timer = setTimeout(createParticles, 500);
    return () => clearTimeout(timer);
  }, []);

  // Efecto de parallax suave al hacer scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const footballBalls = footballBallsRef.current;

      footballBalls.forEach((ball, index) => {
        if (ball) {
          const speed = 0.5 + (index * 0.1);
          ball.style.transform = `translateY(${scrolled * speed}px)`;
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleShareApp = async () => {
    const shareData = {
      title: 'Priball Balones Deportivos',
      text: 'Conocé la app de Priball Balones Deportivos.',
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      await navigator.clipboard.writeText(shareData.url);
      setCopySuccess('¡Enlace copiado con éxito!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error al compartir la app:', err);
    }
  };

  const handleImageUpload = async (index, e) => {
    const file = e.target.files[0];
    if (file) {
      setIsLoading(true);
      try {
        const imageUrl = await uploadImage(file);
        setContent(prev => ({
          ...prev,
          items: prev.items.map((item, i) => 
            i === index 
              ? { ...item, url: imageUrl }
              : item
          )
        }));
      } catch (error) {
        console.error('Error uploading image:', error);
        alert(error.message || 'Error al subir la imagen. Por favor intente nuevamente.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleWhatsAppClick = () => {
    const whatsappUrl = `https://wa.me/${content.whatsapp}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleAddImage = () => {
    setContent(prev => ({
      ...prev,
      items: [...prev.items, {
        id: Date.now() + Math.random(),
        type: 'image',
        url: '',
        link: '',
        tooltip: ''
      }]
    }));
  };

  const handleRemoveImage = (index) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleAddText = () => {
    setContent(prev => ({
      ...prev,
      items: [...prev.items, {
        id: Date.now() + Math.random(),
        type: 'text',
        text: '',
        headerType: 'h2',
        fontType: 'conventional',
        color: '#ffffff',
        link: '',
        tooltip: ''
      }]
    }));
  };

  const handleAddVideo = () => {
    setContent(prev => ({
      ...prev,
      items: [...prev.items, {
        id: Date.now() + Math.random(),
        type: 'video',
        url: '',
        title: '',
        description: ''
      }]
    }));
  };

  const handleRemoveText = (index) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleRemoveVideo = (index) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleTextChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, text: value } : item)
    }));
  };

  const handleTextHeaderChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, headerType: value } : item)
    }));
  };

  const handleTextFontChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, fontType: value } : item)
    }));
  };

  const handleTextColorChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, color: value } : item)
    }));
  };

  const handleTextLinkChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, link: value } : item)
    }));
  };

  const handleTextTooltipChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, tooltip: value } : item)
    }));
  };

  const handleVideoChange = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    
    // Extraer el ID del video de YouTube
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    
    return url;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContent({ ...content, [name]: value });
  };

  const handleImageLinkChange = (index, value) => {
    const newItems = [...content.items];
    
    // Find the image item to update
    const imageItemIndex = newItems.findIndex(item => item.id === newItems[index].id);
    if (imageItemIndex !== -1) {
      newItems[imageItemIndex] = {
        ...newItems[imageItemIndex],
        link: value
      };
    }
    setContent({ ...content, items: newItems });
  };

  const handleImageTooltipChange = (index, value) => {
    const newItems = [...content.items];
    
    // Find the image item to update
    const imageItemIndex = newItems.findIndex(item => item.id === newItems[index].id);
    if (imageItemIndex !== -1) {
      newItems[imageItemIndex] = {
        ...newItems[imageItemIndex],
        tooltip: value
      };
    }
    setContent({ ...content, items: newItems });
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Función helper para obtener la URL correcta de la imagen
  const getImageUrl = (item) => {
    return item.url || '';
  };

  // Función helper para limpiar datos antes de guardar
  const cleanItemData = (item) => {
    const cleanItem = { ...item };
    
    // Limpiar campos de texto
    if (cleanItem.text !== undefined) {
      cleanItem.text = cleanItem.text ? cleanItem.text.trim() : '';
    }
    if (cleanItem.title !== undefined) {
      cleanItem.title = cleanItem.title ? cleanItem.title.trim() : '';
    }
    if (cleanItem.description !== undefined) {
      cleanItem.description = cleanItem.description ? cleanItem.description.trim() : '';
    }
    if (cleanItem.url !== undefined) {
      cleanItem.url = cleanItem.url || '';
    }
    if (cleanItem.link !== undefined) {
      cleanItem.link = cleanItem.link || '';
    }
    if (cleanItem.tooltip !== undefined) {
      cleanItem.tooltip = cleanItem.tooltip || '';
    }
    if (cleanItem.headerType !== undefined) {
      cleanItem.headerType = cleanItem.headerType || 'h2';
    }
    if (cleanItem.fontType !== undefined) {
      cleanItem.fontType = cleanItem.fontType || 'conventional';
    }
    if (cleanItem.color !== undefined) {
      cleanItem.color = cleanItem.color || '#ffffff';
    }
    
    // Asegurar que el ID existe
    if (!cleanItem.id) {
      cleanItem.id = Date.now() + Math.random();
    }
    
    // Asegurar que el tipo existe
    if (!cleanItem.type) {
      cleanItem.type = 'text';
    }
    
    return cleanItem;
  };

  const handleSaveChanges = async () => {
    setIsLoading(true);
    try {
      // Limpiar y validar los datos antes de guardar
      const cleanContent = {
        ...content,
        items: content.items.map(cleanItemData),
        instagram: content.instagram || '',
        facebook: content.facebook || '',
        whatsapp: content.whatsapp || ''
      };

      await setDoc(doc(db, 'HomeInfo', 'main'), cleanContent);
      alert('Cambios guardados correctamente.');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error al guardar los cambios, por favor intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderTextElement = (text, headerType, fontType, color, index, link, tooltip) => {
    const getFontStyle = () => {
      switch (fontType) {
        case 'elegant': return { fontFamily: "'Plus Jakarta Sans', sans-serif", fontStyle: 'italic' };
        case 'bold': return { fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: '700' };
        case 'italic': return { fontFamily: "'DM Sans', sans-serif", fontStyle: 'italic' };
        case 'conventional': default: return { fontFamily: "'Plus Jakarta Sans', sans-serif" };
      }
    };
    
    const baseStyle = { color: color || '#ffffff', ...getFontStyle() };
    
    const textElement = (() => {
      const pStyle = { ...baseStyle, fontFamily: "'DM Sans', sans-serif" };
      switch (headerType) {
        case 'h1':
          return <h1 style={baseStyle}>{text}</h1>;
        case 'h2':
          return <h2 style={baseStyle}>{text}</h2>;
        case 'h3':
          return <h3 style={baseStyle}>{text}</h3>;
        case 'h4':
          return <h4 style={baseStyle}>{text}</h4>;
        case 'p':
          return <p style={pStyle}>{text}</p>;
        default:
          return <h2 style={baseStyle}>{text}</h2>;
      }
    })();

    if (link && isValidUrl(link)) {
      return (
        <a 
          href={link} 
          target="_blank" 
          rel="noopener noreferrer"
          title={tooltip || ''}
          style={{ textDecoration: 'underline', cursor: 'pointer', color: color || '#ffffff' }}
        >
          {textElement}
        </a>
      );
    }

    return textElement;
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      {/* Fondo animado y partículas eliminados, ahora globales */}
      <div className="home">
        <div className="home-logo-container">
          <Link to="/productos">
            <img src={logo} alt="Logo" className="home-logo" />
          </Link>
        </div>

        {/* Controles de administración flotantes */}
        {isEditMode && (
          <div className="floating-admin-controls-container">
            <div className="admin-controls-group">
              <div className="admin-controls">
                <button onClick={handleAddText} className="add-text-button">
                  📝 Agregar Texto
                </button>
                <button onClick={handleAddImage} className="add-image-button">
                  🖼️ Agregar Imagen
                </button>
                <button onClick={handleAddVideo} className="add-video-button">
                  🎥 Agregar Video
                </button>
              </div>
              <button className="floating-save-button" onClick={handleSaveChanges}>
                💾 Guardar Cambios
              </button>
            </div>
          </div>
        )}

        {/* Renderizar textos y videos en contenedores */}
        {content.items && content.items.map((item, index) => (
          (item.type === 'text' || item.type === 'video') && (
            <div key={`item-${item.id}`} className="home-item-container">
              {isEditMode && (
                <button
                  onClick={() => {
                    if (item.type === 'text') handleRemoveText(index);
                    else if (item.type === 'video') handleRemoveVideo(index);
                  }}
                  className="remove-button"
                  title={`Eliminar ${item.type === 'text' ? 'texto' : 'video'}`}
                >
                  ✕
                </button>
              )}

              {/* Renderizar texto */}
              {item.type === 'text' && (
                <>
                  {isEditMode ? (
                    <div className="text-edit-controls">
                      <textarea
                        value={item.text}
                        onChange={(e) => handleTextChange(index, e.target.value)}
                        placeholder="Escribe tu texto aquí..."
                        className="text-content-input"
                      />
                      <div className="text-format-controls">
                        <select
                          value={item.headerType || 'h2'}
                          onChange={(e) => handleTextHeaderChange(index, e.target.value)}
                          className="text-header-select"
                        >
                          <option value="h1">Título Principal (H1)</option>
                          <option value="h2">Título Secundario (H2)</option>
                          <option value="h3">Título Terciario (H3)</option>
                          <option value="p">Párrafo (P)</option>
                        </select>
                        <select
                          value={item.fontType || 'conventional'}
                          onChange={(e) => handleTextFontChange(index, e.target.value)}
                          className="text-font-select"
                        >
                          <option value="conventional">Convencional</option>
                          <option value="elegant">Elegante</option>
                          <option value="bold">Negrita</option>
                          <option value="italic">Cursiva</option>
                        </select>
                        <input
                          type="color"
                          value={item.color || '#ffffff'}
                          onChange={(e) => handleTextColorChange(index, e.target.value)}
                          className="text-color-input"
                          title="Seleccionar color"
                        />
                      </div>
                      <div className="text-link-controls">
                        <input
                          type="text"
                          placeholder="URL completa (ej: https://tuapp.com/producto123)"
                          value={item.link || ''}
                          onChange={(e) => handleTextLinkChange(index, e.target.value)}
                          className={`text-link-input ${item.link && !isValidUrl(item.link) ? 'invalid-url' : ''}`}
                        />
                        {item.link && !isValidUrl(item.link) && (
                          <div className="text-url-error-message">
                            URL inválida
                          </div>
                        )}
                        <input
                          type="text"
                          placeholder="Tooltip (texto que aparece al hacer hover)"
                          value={item.tooltip || ''}
                          onChange={(e) => handleTextTooltipChange(index, e.target.value)}
                          className="text-tooltip-input"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="home-text">
                      {renderTextElement(
                        item.text,
                        item.headerType || 'h2',
                        item.fontType || 'conventional',
                        item.color || '#ffffff',
                        index,
                        item.link || '',
                        item.tooltip || ''
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Renderizar video */}
              {item.type === 'video' && (
                <>
                  {isEditMode ? (
                    <div className="video-edit-controls">
                      <input
                        type="text"
                        placeholder="URL del video de YouTube"
                        value={item.url || ''}
                        onChange={(e) => handleVideoChange(index, 'url', e.target.value)}
                        className="video-url-input"
                      />
                      <input
                        type="text"
                        placeholder="Título del video"
                        value={item.title || ''}
                        onChange={(e) => handleVideoChange(index, 'title', e.target.value)}
                        className="video-title-input"
                      />
                      <textarea
                        placeholder="Descripción del video"
                        value={item.description || ''}
                        onChange={(e) => handleVideoChange(index, 'description', e.target.value)}
                        className="video-description-input"
                      />
                    </div>
                  ) : (
                    <div className="video-container">
                      {item.title && <h3 className="video-title">{item.title}</h3>}
                      {item.description && <p className="video-description">{item.description}</p>}
                      {item.url && (
                        <div className="video-embed">
                          <iframe
                            src={getYouTubeEmbedUrl(item.url)}
                            title={item.title || "Video de YouTube"}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          ></iframe>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )
        ))}

        {/* Grid de imágenes separado */}
        {content.items && content.items.some(item => item.type === 'image') && (
          <div className="home-images-grid">
            {content.items.map((item, index) => (
              item.type === 'image' && (
                <div key={`image-${item.id}`} className="home-image-item">
                  {isEditMode && (
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="remove-button"
                      title="Eliminar imagen"
                    >
                      ✕
                    </button>
                  )}
                  
                  {isEditMode && (
                    <div className="image-upload-container">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(index, e)}
                        title={item.url ? "Reemplazar imagen" : "Seleccionar imagen"}
                        className="image-upload-input"
                        id={`file-input-${index}`}
                      />
                      <label htmlFor={`file-input-${index}`} className="image-upload-label">
                        📷
                      </label>
                    </div>
                  )}
                  
                  {item.url && (
                    <>
                      {item.link && isValidUrl(item.link) ? (
                        <a href={item.link} target="_blank" rel="noopener noreferrer">
                          <img
                            src={getImageUrl(item)}
                            alt="Home"
                            className="home-image clickable"
                            title={item.tooltip || ''}
                          />
                        </a>
                      ) : (
                        <img
                          src={getImageUrl(item)}
                          alt="Home"
                          className="home-image"
                          title={item.tooltip || ''}
                        />
                      )}
                      {isEditMode && (
                        <>
                          <input
                            type="text"
                            placeholder="URL completa (ej: https://tuapp.com/producto123)"
                            value={item.link || ''}
                            onChange={(e) => handleImageLinkChange(index, e.target.value)}
                            className={`image-link-input ${item.link && !isValidUrl(item.link) ? 'invalid-url' : ''}`}
                          />
                          {item.link && !isValidUrl(item.link) && (
                            <div className="url-error-message">
                              URL inválida
                            </div>
                          )}
                          <input
                            type="text"
                            placeholder="Tooltip (texto que aparece al hacer hover)"
                            value={item.tooltip || ''}
                            onChange={(e) => handleImageTooltipChange(index, e.target.value)}
                            className="image-tooltip-input"
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              )
            ))}
          </div>
        )}



        <div className="home-social">
          {isEditMode ? (
            <>
              <input
                type="text"
                name="instagram"
                value={content.instagram}
                onChange={handleInputChange}
                placeholder="Instagram URL"
                className="social-input"
              />
              <input
                type="text"
                name="facebook"
                value={content.facebook}
                onChange={handleInputChange}
                placeholder="Facebook URL"
                className="social-input"
              />
              <input
                type="text"
                name="whatsapp"
                value={content.whatsapp}
                onChange={handleInputChange}
                placeholder="WhatsApp número (con código de país)"
                className="social-input"
              />
            </>
          ) : (
            <div className="social-links">
              <a
                href={content.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="social-logo"
              >
                <img src={instagramLogo} alt="Instagram" style={{ width: '100%', height: '100%' }} />
              </a>
              <a
                href={content.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="social-logo"
              >
                <img src={facebookLogo} alt="Facebook" style={{ width: '100%', height: '100%' }} />
              </a>
              <div
                className="wsocial-logo"
                onClick={handleWhatsAppClick}
                style={{ cursor: 'pointer' }}
              >
                <img src={whatsappLogo} alt="WhatsApp" style={{ width: '100%', height: '100%' }} />
              </div>
            </div>
          )}
        </div>



        {showClientNewsletterSection && (
          <section className="home-subscription-section" aria-label="Newsletter">
            <Subscription />
          </section>
        )}

        <div className="share-app-container">
          <button onClick={handleShareApp} className="share-button" aria-label="Compartir la aplicación">
            <svg className="share-button__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
            <span>Compartir App</span>
          </button>
          {copySuccess && <p className="copy-success">{copySuccess}</p>}
        </div>
      </div>
    </>
  );
};

export default Home;